import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardTitle,
  CardText,
  CardImg,
  Button,
  Row,
  Col,
} from "reactstrap";
import styles from "./styles.scss";
import { serviceCaller, ServiceCallerProps } from "../../composition";
import withStyles from "isomorphic-style-loader/withStyles";
import donorPerfectLogo from "../../../media/img/donorperfect_logo.png";
import virtuousLogo from "../../../media/img/virtuous_logo.png";
import firebase from "firebase/compat/app";
import { get } from "lodash";
import DonorPerfect from "./donorPerfect";
import { firebaseConnect } from "react-redux-firebase";
import { connect } from "react-redux";
import Virtuous from "./virtuous";
import { PulseLoader } from "react-spinners";
import { Segment } from "./common/segmentsPills/types";

type AvailableIntegrations = "donorPerfect" | "virtuous";

interface Props extends ServiceCallerProps {
  firebase: firebase.app.App;
  isConnectedDonorPerfect: boolean;
  isConnectedVirtuous: boolean;
  isDonorPerfectSyncing: boolean;
  isVirtuousConnecting: boolean;
  virtuousApiKey: string;
  virtuousWebhookId: number;
  selectedVirtuousTags: Segment[];
  enableVirtuous: boolean;
  enableDonorPerfect: boolean;
}

interface IntegrationCardProps {
  id: AvailableIntegrations;
  name: string;
  description: string;
  image: string;
  isConnected: boolean;
  isConnecting: boolean;
  onClick: () => void;
}

const getIntegrationsList = (enableDonorPerfect, enableVirtuous) => [
  {
    id: "donorPerfect" as AvailableIntegrations,
    name: "DonorPerfect",
    description:
      "Connect DonorPerfect and easily sync your contacts to ViewSpark.",
    image: donorPerfectLogo,
    available: enableDonorPerfect,
  },
  {
    id: "virtuous" as AvailableIntegrations,
    name: "Virtuous",
    description: "Connect Virtuous and easily sync your contacts to ViewSpark.",
    image: virtuousLogo,
    available: enableVirtuous,
  },
];

const mapStateToProps = (state, _) => {
  const { firebase, user } = state;

  const config = `data.${user.currentCharity.id}.config`;
  const integrations = `data.${user.currentCharity.id}.internal.integrations`;

  const enableVirtuous = get(firebase, `${config}.enableVirtuous`, false);
  const enableDonorPerfect = get(
    firebase,
    `${config}.enableDonorPerfect`,
    false
  );

  const isConnectedDonorPerfect = get(
    firebase,
    `${integrations}.donorPerfectConfig.connected`,
    false
  );
  const isConnectedVirtuous = get(
    firebase,
    `${integrations}.virtuousConfig.connected`,
    false
  );
  const virtuousApiKey = get(firebase, `${integrations}.virtuousConfig.apiKey`, "");
  const virtuousWebhookId = get(
    firebase,
    `${integrations}.virtuousConfig.webhookId`,
    ""
  );
  const isDonorPerfectSyncing =
    get(
      firebase,
      `${integrations}.donorPerfectConfig.syncConfig.runningSyncStart`,
      0
    ) > 0;
  const isVirtuousConnecting = get(
    firebase,
    `${integrations}.virtuousConfig.connecting`,
    false
  );
  const selectedVirtuousTags = get(
    firebase,
    `${integrations}.virtuousConfig.connectedSegments`,
    [] as Segment[]
  ) as Segment[];

  return {
    isConnectedDonorPerfect,
    isConnectedVirtuous,
    isDonorPerfectSyncing,
    virtuousApiKey,
    virtuousWebhookId,
    isVirtuousConnecting,
    selectedVirtuousTags,
    enableVirtuous,
    enableDonorPerfect,
  };
};

const IntegrationCard = ({
  id,
  name,
  description,
  image,
  isConnected,
  isConnecting,
  onClick,
}: IntegrationCardProps) => {
  return (
    <Card className={styles.integrationCard} key={id}>
      <CardBody>
        <Row>
          <Col xs={12}>
            <CardImg
              top
              className={`${styles.integrationImg} mb-3`}
              src={image}
              style={{ width: "50px", height: "50px", borderRadius: "50%" }}
            />
          </Col>
          <Col xs={12}>
            <CardTitle style={{fontWeight: "bold"}}>{name}</CardTitle>
            <CardText style={{fontWeight: "200"}}>{description}</CardText>
          </Col>
        </Row>
        {isConnected ? (
          <Button
            className={`${styles.settingsButton}`}
            onClick={onClick}
            block
          >
            Integration Settings
          </Button>
        ) : isConnecting ? (
          <Button className={`${styles.settingsButton}`} block>
            <PulseLoader color="#1F65B1" loading={true} />
          </Button>
        ) : (
          <Button className={`${styles.connectButton}`} onClick={onClick} block>
            Connect
          </Button>
        )}
      </CardBody>
    </Card>
  );
};

const Integrations = (props: Props) => {
  const {
    isConnectedDonorPerfect,
    isConnectedVirtuous,
    isDonorPerfectSyncing,
    virtuousApiKey,
    virtuousWebhookId,
    isVirtuousConnecting,
    selectedVirtuousTags,
    enableDonorPerfect,
    enableVirtuous,
    currentCharity,
  } = props;

  const charityId = currentCharity?.id;

  const getConnectionStatus = (integration: AvailableIntegrations) => {
    switch (integration) {
      case "donorPerfect":
        return isConnectedDonorPerfect;
      case "virtuous":
        return isConnectedVirtuous;
      default:
        return false;
    }
  };

  const getSyncStatus = (integration: AvailableIntegrations) => {
    switch (integration) {
      case "donorPerfect":
        return isDonorPerfectSyncing;
      case "virtuous":
        return isVirtuousConnecting;
      default:
        return false;
    }
  };

  const availableIntegrations = getIntegrationsList(
    enableDonorPerfect,
    enableVirtuous
  );

  const [selectedIntegration, setSelectedIntegration] = useState<
    AvailableIntegrations
  >(null);

  useEffect(() => {
    const enabledIntegrations = getIntegrationsList(
      enableDonorPerfect,
      enableVirtuous
    ).filter(integration => integration.available);

    if (enabledIntegrations.length === 1) {
      setSelectedIntegration(enabledIntegrations[0].id);
      return;
    }

    setSelectedIntegration(null);
  }, [charityId, enableDonorPerfect, enableVirtuous]);

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        {selectedIntegration ? (
          selectedIntegration === "donorPerfect" ? (
            <DonorPerfect
              key={charityId}
              onBack={() => setSelectedIntegration(null)}
            />
          ) : selectedIntegration === "virtuous" ? (
            <Virtuous
              key={charityId}
              onBack={() => setSelectedIntegration(null)}
              isConnected={getConnectionStatus(availableIntegrations[1].id)}
              isConnecting={isVirtuousConnecting}
              connectedApiKey={virtuousApiKey}
              webhookId={virtuousWebhookId}
              selectedTags={selectedVirtuousTags}
            />
          ) : (
            <div>Something wrong happened</div>
          )
        ) : (
          <Row
            className={`${styles.integrationListContainer} justify-content-center`}
          >
            {availableIntegrations.map(
              integration =>
                integration.available && (
                  <Col key={integration.id} xs={12} sm={8} md={6} lg={4}>
                    <IntegrationCard
                      {...integration}
                      isConnected={getConnectionStatus(integration.id)}
                      isConnecting={getSyncStatus(integration.id)}
                      onClick={() => {
                        setSelectedIntegration(integration.id);
                      }}
                    />
                  </Col>
                )
            )}
          </Row>
        )}
      </div>
    </main>
  );
};

export default serviceCaller(
  connect(mapStateToProps)(
    firebaseConnect((_, store) => {
      const charityId = store.getState().user.currentCharity?.id;
      if (!charityId) {
        return [];
      }
      return [
        `${charityId}/internal/integrations/virtuousConfig`,
        `${charityId}/internal/integrations/donorPerfectConfig`,
        `${charityId}/config/enableVirtuous`,
        `${charityId}/config/enableDonorPerfect`,
      ];
    })(withStyles(styles)(Integrations))
  )
);
